type ConsentGateParams = {
  log: (message: string) => void
}

export type ConsentGate = {
  // The iframe's decision so far. true means enforce consent: no provider reads, batches dropped,
  // deanonymize() resolves at once. Unknown until the first Consent_Decision arrives.
  isEnforced: () => boolean
  setDecision: (enforce: boolean) => void
  // From init once the provider settings are loaded, with the provider subscription. It runs while
  // the decision is to proceed and returns what stops it: a decision to enforce stops the reads, a
  // later decision to proceed runs it again.
  collectionReady: (startCollection: () => () => void) => void
  // Per provider batch: false means the batch is dropped, not submitted and not sent to tracking.
  allowsBatch: () => boolean
}

/**
 * The embed's side of the consent decision. The decision itself is made in the iframe, which knows
 * the tenant's flag; the embed reports the visitor's consent there and carries out what comes back.
 * Nothing is read before the first decision, so a denied visitor is never read while the iframe is
 * still working out whether the tenant applies consent.
 */
export const createConsentGate = ({ log }: ConsentGateParams): ConsentGate => {
  let decision: boolean | null = null
  let start: (() => () => void) | null = null
  let stopCollection: (() => void) | null = null
  let started = false

  const tryStart = (): void => {
    if (decision !== false || started || !start) return
    started = true
    stopCollection = start()
  }

  // Ends the provider reads a start began, so an enforced visitor's providers are no longer read,
  // rather than read and dropped. A later decision to proceed starts them again.
  const stop = (): void => {
    if (!started) return
    started = false
    stopCollection?.()
    stopCollection = null
  }

  return {
    isEnforced: () => decision === true,
    setDecision: enforce => {
      decision = enforce
      if (enforce) {
        stop()
      }
      log(
        enforce
          ? 'Consent enforced — deanonymization providers are not read and batches are dropped'
          : 'Deanonymization proceeds'
      )
      tryStart()
    },
    collectionReady: startCollection => {
      start = startCollection
      tryStart()
    },
    allowsBatch: () => {
      if (decision !== false) {
        log('Consent enforced — deanonymization batch dropped')
      }
      return decision === false
    },
  }
}
